// Smart Sash Admin Panel - Generic Activation Flow Hook
// Reusable gRPC subscription + verification code + countdown logic
// Used by both RFID Assignment and Device Registration flows

import { ChannelType } from '@/generated/Common/EventService';
import { VerificationCodeSentEvent } from '@/generated/Common/VerificationEvents';
import { grpcEventService } from '@/services/grpc';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ActivationStatus =
  | 'loading'
  | 'waiting'
  | 'expired'
  | 'success'
  | 'error'
  | 'aborted'
  | 'cancelled';

export interface ActivationFlowState {
  status: ActivationStatus;
  verificationCode: string | null;
  verificationType: string | null;
  timeRemaining: number;
  errorMessage: string | null;
  sessionClosed: boolean;
  cancel: () => Promise<void>;
}

interface EventDecoders {
  /** Decode the confirmation event and return true if successful */
  decodeConfirmation: (data: Uint8Array) => boolean;
  /** Decode the cancellation event (returns void, just validates) */
  decodeCancellation: (data: Uint8Array) => void;
}

export interface ActivationFlowConfig {
  /** gRPC event names to listen for confirmation (e.g. CardAssignmentConfirmationEvent) */
  confirmationEvents: string[];
  /** gRPC event name for cancellation */
  cancellationEvent: string;
  /** Protobuf decoders for confirmation and cancellation events */
  decoders: EventDecoders;
  /** API call to trigger the activation request */
  requestFn: (params: {
    institutionId: string;
    sessionId: string;
    labId?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  /** API call to revoke/cancel the request */
  revokeFn: (params: { institutionId: string; sessionId: string; labId?: string }) => Promise<void>;
  /** Cancellation message shown to user */
  cancelledMessage?: string;
}

const GRPC_URL = import.meta.env.VITE_GRPC_URL || 'https://localhost:5001';

/**
 * Generic activation flow hook that manages gRPC subscription,
 * verification code display, countdown timer, and cancel/revoke logic.
 */
export const useActivationFlow = (
  sessionId: string | null,
  institutionId: string | null,
  labId: string | null,
  config: ActivationFlowConfig
): ActivationFlowState => {
  const [status, setStatus] = useState<ActivationStatus>('loading');
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const [verificationType, setVerificationType] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionClosed, setSessionClosed] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiresAtRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(
    (expiresAtSeconds: number) => {
      expiresAtRef.current = expiresAtSeconds;
      clearTimer();

      const tick = () => {
        const remaining = Math.max(0, Math.floor(expiresAtRef.current - Date.now() / 1000));
        setTimeRemaining(remaining);
        if (remaining === 0) {
          clearTimer();
          setStatus((prev) => (prev === 'waiting' ? 'expired' : prev));
        }
      };

      tick();
      timerRef.current = setInterval(tick, 1000);
    },
    [clearTimer]
  );

  useEffect(() => {
    if (!sessionId || !institutionId) return;

    let disposed = false;
    setSessionClosed(false);

    const run = async () => {
      try {
        grpcEventService.initialize(GRPC_URL);

        const onVerificationCode = (rawData: unknown) => {
          if (disposed) return;
          try {
            const event = VerificationCodeSentEvent.decode(rawData as Uint8Array);
            setVerificationCode(event.verificationCode);
            setVerificationType(event.verificationType);
            startCountdown(event.expiresAt);
            setStatus('waiting');
          } catch (e) {
            console.error('Failed to decode VerificationCodeSentEvent:', e);
            setErrorMessage('Failed to decode verification code event');
            setStatus('error');
          }
        };

        const onConfirmation = (rawData: unknown) => {
          if (disposed) return;
          try {
            const success = config.decoders.decodeConfirmation(rawData as Uint8Array);
            if (success) {
              clearTimer();
              setStatus('success');
            }
          } catch (e) {
            console.error('Failed to decode confirmation event:', e);
          }
        };

        const onCancellation = (rawData: unknown) => {
          if (disposed) return;
          try {
            config.decoders.decodeCancellation(rawData as Uint8Array);
            clearTimer();
            setStatus((prev) => {
              if (prev === 'success') return prev;
              setErrorMessage(config.cancelledMessage ?? 'Registration was cancelled by the user');
              return 'cancelled';
            });
          } catch (e) {
            console.error('Failed to decode cancellation event:', e);
          }
        };

        grpcEventService.on('VerificationCodeSentEvent', onVerificationCode);
        config.confirmationEvents.forEach((name) => grpcEventService.on(name, onConfirmation));
        grpcEventService.on(config.cancellationEvent, onCancellation);

        await grpcEventService.subscribe(sessionId, institutionId, ChannelType.ADMIN);

        const response = await config.requestFn({ institutionId, sessionId, ...(labId && { labId }) });
        if (!response.success) {
          throw new Error(response.error || 'Failed to request activation');
        }
      } catch (err) {
        if (!disposed) {
          const msg = err instanceof Error ? err.message : 'Activation failed';
          setErrorMessage(msg);
          setStatus('error');
        }
      }
    };

    run();

    return () => {
      disposed = true;
      clearTimer();
      grpcEventService.off('VerificationCodeSentEvent');
      config.confirmationEvents.forEach((name) => grpcEventService.off(name));
      grpcEventService.off(config.cancellationEvent);
      grpcEventService.unsubscribe().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, institutionId, labId, startCountdown, clearTimer]);

  const cancel = useCallback(async () => {
    clearTimer();
    if (sessionId && institutionId) {
      try {
        await config.revokeFn({ institutionId, sessionId, ...(labId && { labId }) });
      } catch {
        // Ignore revoke errors — show aborted screen regardless
      }
    }
    setSessionClosed(true);
    setStatus('aborted');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, institutionId, labId, clearTimer]);

  return {
    status,
    verificationCode,
    verificationType,
    timeRemaining,
    errorMessage,
    sessionClosed,
    cancel,
  };
};
